import { Divider, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { v4 as newGuid } from "uuid";
import { DrawerLayout, MeetingNotesTabPanel, SignalRGroup } from ".";
import { useSession, useSignalR } from "../hooks";
import {
  queryKeys,
  useCreateMeetingNote,
  useGetMeeting,
  useGetPrivateMeetingNotes,
  useGetSharedMeetingNotes,
} from "../http";
import { MeetingNotesTab } from "src/constants";

interface IProps {
  meetingGuid: string;
}

const MeetingNotesDrawerContent = (props: IProps) => {
  const { displayName, userId, profilePhotoSmallUrl } = useSession();
  const { mutate: createMeetingNote } = useCreateMeetingNote();
  const signalR = useSignalR();
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState<MeetingNotesTab>(MeetingNotesTab.Shared);
  const meeting = useGetMeeting(props.meetingGuid);
  const sharedNotes = useGetSharedMeetingNotes(props.meetingGuid);
  const privateNotes = useGetPrivateMeetingNotes(props.meetingGuid);

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.MeetingNotes(props.meetingGuid));
    return () => signalR.leaveGroup(SignalRGroup.MeetingNotes(props.meetingGuid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.meetingGuid]);

  useEffect(() => {
    const handleShouldRefetchMeetingNotes = (meetingGuid: string) => {
      queryClient.invalidateQueries(queryKeys.meetings.getSharedMeetingNotes(meetingGuid));
    };

    signalR.onShouldRefetchMeetingNotes(handleShouldRefetchMeetingNotes);
    return () => signalR.offShouldRefetchMeetingNotes(handleShouldRefetchMeetingNotes);
  }, [queryClient, signalR]);

  function handleSendClicked(text: string, isPrivate: boolean) {
    createMeetingNote(
      {
        meetingGuid: props.meetingGuid,
        meetingNoteGuid: newGuid(),
        createdByUser: { displayName, enabled: true, profilePhotoSmallUrl, userId },
        text,
        isPrivate,
      },
      {
        onSuccess: () => {
          if (!isPrivate) {
            signalR.sendRefetchMeetingNotes(props.meetingGuid);
          }
        },
      },
    );
  }

  return (
    <DrawerLayout
      title={
        <Stack>
          <Typography variant="h6">Meeting Notes</Typography>
          {meeting.data && (
            <Typography variant="caption">
              {meeting.data.type === "Team"
                ? `${meeting.data.team.name} - ${meeting.data.title}`
                : `1:1 ${meeting.data.otherUser.displayName}, ${meeting.data.leadUser.displayName}`}
            </Typography>
          )}
        </Stack>
      }
    >
      <Tabs variant="fullWidth" value={currentTab} onChange={(_, tab) => setCurrentTab(tab)}>
        <Tab label="Shared" value={MeetingNotesTab.Shared} disableRipple />
        <Tab label="Private" value={MeetingNotesTab.Private} disableRipple />
      </Tabs>
      <Divider />
      {currentTab === MeetingNotesTab.Shared && (
        <MeetingNotesTabPanel
          meetingGuid={props.meetingGuid}
          data={sharedNotes.data}
          isLoading={sharedNotes.isLoading}
          isPrivate={false}
          onSendClicked={(text) => handleSendClicked(text, false)}
        />
      )}
      {currentTab === MeetingNotesTab.Private && (
        <MeetingNotesTabPanel
          meetingGuid={props.meetingGuid}
          data={privateNotes.data}
          isLoading={privateNotes.isLoading}
          isPrivate={true}
          onSendClicked={(text) => handleSendClicked(text, true)}
        />
      )}
    </DrawerLayout>
  );
};

export { MeetingNotesDrawerContent };
